import { memo, MutableRefObject, useEffect, useRef } from "react";
import { Box, useTheme } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { styless } from "../../pages/suporte/styles";

const MODULES = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link", "clean"],
  ],
  clipboard: { matchVisual: false },
};

const FORMATS = [
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
  "image",
];

interface RichEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder: string;
  disabled?: boolean;
  compacto?: boolean;
  autoFoco?: boolean;
  onPaste?: (e: ClipboardEvent) => void;
  inserirImagensRef?: MutableRefObject<((urls: string[]) => void) | null>;
}

function RichEditorBase({
  value,
  onChange,
  placeholder,
  disabled,
  compacto,
  autoFoco,
  onPaste,
  inserirImagensRef,
}: RichEditorProps) {
  const theme = useTheme();
  const styles = styless(theme);
  const quillRef = useRef<ReactQuill | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const onPasteRef = useRef(onPaste);
  onPasteRef.current = onPaste;

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    // O StrictMode monta o editor duas vezes e o Quill pode deixar a primeira
    // toolbar órfã no DOM.
    const toolbars = wrapperRef.current?.querySelectorAll(".ql-toolbar");
    if (toolbars && toolbars.length > 1) {
      for (let i = 0; i < toolbars.length - 1; i++) toolbars[i].remove();
    }

    const handlePaste = (e: ClipboardEvent) => onPasteRef.current?.(e);
    quill.root.addEventListener("paste", handlePaste, true);

    if (inserirImagensRef) {
      inserirImagensRef.current = (urls) => {
        let indice = quill.getSelection(true)?.index ?? quill.getLength();
        urls.forEach((url) => {
          quill.insertEmbed(indice, "image", url, "user");
          indice += 1;
        });
        quill.setSelection(indice, 0, "user");
      };
    }

    if (autoFoco) {
      quill.setSelection(quill.getLength(), 0, "user");
    }

    return () => {
      if (inserirImagensRef) inserirImagensRef.current = null;
      quill.root.removeEventListener("paste", handlePaste, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      ref={wrapperRef}
      sx={compacto ? { ...styles.editor, ...styles.editorCompacto } : styles.editor}
    >
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={MODULES}
        formats={FORMATS}
        placeholder={placeholder}
        readOnly={disabled}
      />
    </Box>
  );
}

export default memo(RichEditorBase);
